import { MRT_ColumnFiltersState } from "material-react-table";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { SearchValue } from "src/api/_common/hydra/search";
import { RangeValue } from "src/components/Grid/filters/RangeFilter";
import { ComparisonValue } from "src/components/Grid/filters/ComparisonFilter";
import { EmptyNullValue } from "src/components/Grid/filters/EmptyNullFilter";
import type { ScoreFilterValue } from "src/components/Grid/filters/ScoreFilter";
import type { TagsFilterValue } from "src/components/Grid/filters/TagsFilter";
import type { JsonAttributeFilterValue } from "src/components/Grid/filters/JsonAttributeFilter";

// export const TAG_TYPE_PREFIX

export const GRID_EMPTY_FILTER: MRT_ColumnFiltersState = [];

const mrtFiltersToUrl = (
    initialSearchParam: URLSearchParams,
    columnFilters: MRT_ColumnFiltersState,
    defaultValues: UrlFilterParams,
): URLSearchParams => {
    const newFiltersParams = new URLSearchParams(initialSearchParam);

    // Values remaining in this originalValues are cleared from URL at end.
    const originalValues = new Set(Object.keys(defaultValues));

    // Track tag parameters to clear old ones
    const existingTagParams = new Set<string>();

    // Track all filter keys in URL (including those with operators like score-1[gte])
    // This allows us to clean up removed filters that aren't in defaultValues
    const existingFilterKeys = new Set<string>();

    // Track jsonAttributes_ parameters to clear old ones
    const existingJsonAttributeParams = new Set<string>();

    // Track array bracket params (e.g. "productLine.name[]") to clear old ones
    const existingBracketParams = new Set<string>();

    // Also track which filters are in the new columnFilters to avoid removing newly added ones
    const activeFilterKeys = new Set<string>();
    columnFilters.forEach((filter) => {
        activeFilterKeys.add(filter.id);
    });

    newFiltersParams.forEach((value, key) => {
        if (key.startsWith("tag_")) {
            existingTagParams.add(key);
            return;
        }

        // Track bracket array params (e.g. "productLine.name[]" => base key "productLine.name")
        if (key.endsWith("[]")) {
            const baseKey = key.slice(0, -2);
            if (!activeFilterKeys.has(baseKey)) {
                existingBracketParams.add(baseKey);
            }
            return;
        }

        // Track jsonAttributes_ parameters
        const jsonAttrMatch = key.match(/^(jsonAttributes_[^[]+)/);
        if (jsonAttrMatch) {
            const baseKey = jsonAttrMatch[1];
            // Only track for cleanup if it's not in the active filters
            if (!activeFilterKeys.has(baseKey)) {
                existingJsonAttributeParams.add(baseKey);
            }
            return;
        }

        // Extract base key from operator format (e.g., "score-1[gte]" => "score-1")
        const operatorMatch = key.match(/^(.+)\[(gt|gte|lt|lte|eq|empty|notEmpty)\]$/);
        if (operatorMatch) {
            const baseKey = operatorMatch[1];
            // Only track for cleanup if it's not in the active filters
            if (!activeFilterKeys.has(baseKey)) {
                existingFilterKeys.add(baseKey);
            }
        }

        // Also track referenceScore-* filters specifically
        if (key.startsWith("referenceScore-")) {
            const refScoreMatch = key.match(/^(referenceScore-\d+)/);
            if (refScoreMatch) {
                const baseKey = refScoreMatch[1];
                if (!activeFilterKeys.has(baseKey)) {
                    existingFilterKeys.add(baseKey);
                }
            }
        }

        // Also track scoreComparison-* filters specifically
        if (key.startsWith("scoreComparison-")) {
            const comparisonMatch = key.match(/^(scoreComparison-\d+)/);
            if (comparisonMatch) {
                const baseKey = comparisonMatch[1];
                if (!activeFilterKeys.has(baseKey)) {
                    existingFilterKeys.add(baseKey);
                }
            }
        }
    });

    columnFilters.forEach((columnFilter) => {
        const key = columnFilter.id;
        const value = columnFilter.value;

        // Handle tag filters (tag_typeSlug) - can be TagsFilterValue or plain string/array
        if (key.startsWith("tag_")) {
            // We don't want to `use` a value, that has been deleted.
            if (value === null) {
                return;
            }
            existingTagParams.delete(key); // Don't clear this one at the end
            existingTagParams.delete(`${key}[empty]`); // Also keep operator variants
            existingTagParams.delete(`${key}[notEmpty]`);
            originalValues.delete(key); // Remove from cleanup list

            // Clear existing params
            newFiltersParams.delete(key);
            newFiltersParams.delete(`${key}[empty]`);
            newFiltersParams.delete(`${key}[notEmpty]`);

            // Check if TagsFilterValue format
            if (
                value &&
                typeof value === "object" &&
                !Array.isArray(value) &&
                ("values" in value || "emptyNull" in value)
            ) {
                const tagsFilter = value as TagsFilterValue;

                // Set values params
                if (tagsFilter.values) {
                    if (Array.isArray(tagsFilter.values)) {
                        const stringValues = tagsFilter.values.map((v) => (typeof v === "string" ? v : String(v)));
                        newFiltersParams.set(key, stringValues.join(","));
                    } else {
                        newFiltersParams.set(key, tagsFilter.values);
                    }
                }

                // Set empty/notEmpty params
                if (tagsFilter.emptyNull === "empty") {
                    newFiltersParams.set(`${key}[empty]`, "true");
                } else if (tagsFilter.emptyNull === "notEmpty") {
                    newFiltersParams.set(`${key}[notEmpty]`, "true");
                }
            } else if (Array.isArray(value)) {
                // Multiple values: join with comma (no escaping needed)
                const stringValues = value.map((v) => (typeof v === "string" ? v : String(v)));
                newFiltersParams.set(key, stringValues.join(","));
            } else if (typeof value === "string") {
                // Single value or EmptyNullValue
                if (value === "empty") {
                    newFiltersParams.set(`${key}[empty]`, "true");
                } else if (value === "notEmpty") {
                    newFiltersParams.set(`${key}[notEmpty]`, "true");
                } else {
                    newFiltersParams.set(key, value);
                }
            }
            return;
        }

        // Handle ScoreFilterValue (combined range + empty/notEmpty)
        if (value && typeof value === "object" && ("min" in value || "max" in value || "emptyNull" in value)) {
            const scoreFilter = value as ScoreFilterValue;

            // Clear existing params for this key
            newFiltersParams.delete(`${key}[gte]`);
            newFiltersParams.delete(`${key}[lte]`);
            newFiltersParams.delete(`${key}[empty]`);
            newFiltersParams.delete(`${key}[notEmpty]`);

            // Set range params
            if (scoreFilter.min !== undefined && scoreFilter.min !== "") {
                newFiltersParams.set(`${key}[gte]`, String(scoreFilter.min));
            }
            if (scoreFilter.max !== undefined && scoreFilter.max !== "") {
                newFiltersParams.set(`${key}[lte]`, String(scoreFilter.max));
            }

            // Set empty/notEmpty params
            if (scoreFilter.emptyNull === "empty") {
                newFiltersParams.set(`${key}[empty]`, "true");
            } else if (scoreFilter.emptyNull === "notEmpty") {
                newFiltersParams.set(`${key}[notEmpty]`, "true");
            }

            originalValues.delete(key);
            existingFilterKeys.delete(key);
            return;
        }

        // Handle ComparisonValue filters (for comparison operators)
        if (value && typeof value === "object" && "operator" in value && "value" in value) {
            const compValue = value as ComparisonValue;

            // Clear any existing comparison params for this key
            ["gt", "gte", "lt", "lte", "eq"].forEach((op) => {
                newFiltersParams.delete(`${key}[${op}]`);
            });

            if (compValue.value !== undefined && compValue.value !== "") {
                newFiltersParams.set(`${key}[${compValue.operator}]`, String(compValue.value));
            }
            originalValues.delete(key);
            existingFilterKeys.delete(key); // Mark as handled
            return;
        }

        // Handle JsonAttributeFilterValue (for jsonAttributes_ prefixed keys)
        if (key.startsWith("jsonAttributes_")) {
            // If value is null or undefined, clear all related params
            if (!value) {
                newFiltersParams.delete(key);
                newFiltersParams.delete(`${key}[gte]`);
                newFiltersParams.delete(`${key}[lte]`);
                newFiltersParams.delete(`${key}[empty]`);
                newFiltersParams.delete(`${key}[notEmpty]`);
                originalValues.delete(key);
                existingFilterKeys.delete(key);
                return;
            }

            // Handle object value (JsonAttributeFilterValue)
            if (
                typeof value === "object" &&
                ("value" in value || "min" in value || "max" in value || "emptyNull" in value)
            ) {
                const jsonAttrFilter = value as JsonAttributeFilterValue;

                // Clear existing params
                newFiltersParams.delete(key);
                newFiltersParams.delete(`${key}[gte]`);
                newFiltersParams.delete(`${key}[lte]`);
                newFiltersParams.delete(`${key}[empty]`);
                newFiltersParams.delete(`${key}[notEmpty]`);

                // Set value param (text search)
                if (jsonAttrFilter.value) {
                    newFiltersParams.set(key, jsonAttrFilter.value);
                }

                // Set range params (for numeric types)
                if (jsonAttrFilter.min !== undefined && jsonAttrFilter.min !== "") {
                    newFiltersParams.set(`${key}[gte]`, String(jsonAttrFilter.min));
                }
                if (jsonAttrFilter.max !== undefined && jsonAttrFilter.max !== "") {
                    newFiltersParams.set(`${key}[lte]`, String(jsonAttrFilter.max));
                }

                // Set empty/notEmpty params
                if (jsonAttrFilter.emptyNull === "empty") {
                    newFiltersParams.set(`${key}[empty]`, "true");
                } else if (jsonAttrFilter.emptyNull === "notEmpty") {
                    newFiltersParams.set(`${key}[notEmpty]`, "true");
                }

                originalValues.delete(key);
                existingFilterKeys.delete(key);
                return;
            }
        }

        // Handle EmptyNullValue filters (for empty/notEmpty)
        if (typeof value === "string" && (value === "empty" || value === "notEmpty")) {
            const emptyNullValue = value as EmptyNullValue;

            // Clear existing empty/notEmpty params for this key
            newFiltersParams.delete(`${key}[empty]`);
            newFiltersParams.delete(`${key}[notEmpty]`);

            if (emptyNullValue === "empty") {
                newFiltersParams.set(`${key}[empty]`, "true");
            } else if (emptyNullValue === "notEmpty") {
                newFiltersParams.set(`${key}[notEmpty]`, "true");
            }
            originalValues.delete(key);
            existingFilterKeys.delete(key); // Mark as handled
            return;
        }

        // Handle regular filters (from defaultValues)
        if (Array.isArray(value)) {
            const values = value;

            // Delete both formats to avoid duplicates
            newFiltersParams.delete(key);
            newFiltersParams.delete(`${key}[]`);

            values.forEach((v) => {
                const originalValue = defaultValues[key] as unknown as string[];
                if (originalValue !== v) {
                    // Use brackets notation for arrays (PHP standard)
                    const keyWithBrackets = `${key}[]`;
                    const isAlreadyHere = newFiltersParams.has(keyWithBrackets, v);
                    if (isAlreadyHere === false) {
                        newFiltersParams.append(keyWithBrackets, v);
                        return;
                    }
                }
            });
            originalValues.delete(key);
        } else if (typeof value === "string") {
            const originalValue = defaultValues[key] as unknown as string;
            if (originalValue !== value) {
                newFiltersParams.set(key, value);
                originalValues.delete(key);
            }
        }

        // Clean used values from defaultValues
        originalValues.delete(key);
    });

    // Clear remaining original values
    originalValues.forEach((v) => {
        newFiltersParams.delete(v);

        // Also clear any comparison/range operator variants for this key
        // This handles filters that were removed completely from columnFilters
        ["gt", "gte", "lt", "lte", "eq", "empty", "notEmpty"].forEach((op) => {
            newFiltersParams.delete(`${v}[${op}]`);
        });
    });

    // Clear remaining tag parameters that weren't updated
    existingTagParams.forEach((tagParam) => {
        newFiltersParams.delete(tagParam);
    });

    // Clear remaining jsonAttribute parameters that weren't updated
    existingJsonAttributeParams.forEach((jsonAttrParam) => {
        newFiltersParams.delete(jsonAttrParam);
        newFiltersParams.delete(`${jsonAttrParam}[gte]`);
        newFiltersParams.delete(`${jsonAttrParam}[lte]`);
        newFiltersParams.delete(`${jsonAttrParam}[empty]`);
        newFiltersParams.delete(`${jsonAttrParam}[notEmpty]`);
    });

    // Clear remaining bracket array parameters that weren't updated (e.g. productLine.name[])
    existingBracketParams.forEach((baseKey) => {
        newFiltersParams.delete(`${baseKey}[]`);
        // Also delete without brackets just in case
        newFiltersParams.delete(baseKey);
    });

    // Clear remaining filter keys that weren't updated (e.g., removed score filters)
    // This handles dynamic filters like score-1[gte], score-2[lte], etc.
    existingFilterKeys.forEach((filterKey) => {
        ["gt", "gte", "lt", "lte", "eq", "empty", "notEmpty"].forEach((op) => {
            newFiltersParams.delete(`${filterKey}[${op}]`);
        });
    });

    return newFiltersParams;
};

// Probably in Store ? Like Zustand, or just a typed resource
export type UrlFilterParams = Record<
    string,
    {
        value: SearchValue;
    }
>;

const urlToMrtFilters = (filterString: URLSearchParams, defaultValues: UrlFilterParams): MRT_ColumnFiltersState => {
    const acc: MRT_ColumnFiltersState = [];

    const columnFilters = Object.entries(defaultValues).reduce((acc, [field, item]) => {
        const { value } = item;
        if (typeof value === "string") {
            const urlValue = filterString.get(field);
            if (urlValue === null) return acc;

            acc.push({ id: field, value: urlValue });
            return acc;
        }

        if (Array.isArray(value)) {
            // Try with brackets first (PHP standard for arrays)
            let urlValues = filterString.getAll(`${field}[]`);
            // Fallback to without brackets for backward compatibility
            if (urlValues.length === 0) {
                urlValues = filterString.getAll(field);
            }
            if (urlValues.length === 0) return acc;

            // Special handling for tag filters: split comma-separated values
            if (field.startsWith("tag_") && urlValues.length === 1 && urlValues[0].includes(",")) {
                const splitValues = urlValues[0]
                    .split(",")
                    .map((v) => v.trim())
                    .filter((v) => v.length > 0);
                acc.push({ id: field, value: splitValues.length === 1 ? splitValues[0] : splitValues });
            } else {
                acc.push({ id: field, value: urlValues });
            }
            return acc;
        }

        // // Not sure I want to alter that right now
        // // Current system is    : tag_tag1=coutag1
        // // ?? Next one is       : tags[tag1]=coutag1
        if (typeof value === "object") {
            // do nothing, it's just a type that is accepted. But no fields uses it yet
            // EXCEPT now tags, but tags is a very special case.
            // Maybe this `value === "object"` is just a special case. I'm not sure yet
        }

        return acc;
    }, acc);

    // Extract tag filters from URL parameters (format: tag_typeSlug=value1,value2)
    // If we ever want to make a system that hasn't got an EXCEPTION here,
    // we can use the typeof "object" above.
    // Just know it won't be fast/easy.
    // Track which tags have already been added from defaultValues to avoid duplicates
    const addedTagKeys = new Set(columnFilters.filter((f) => f.id.startsWith("tag_")).map((f) => f.id));
    const processedTagKeys = new Set<string>();

    filterString.forEach((value, key) => {
        // Process plain tag keys (e.g. tag_phase-projet=val1,val2)
        // Also handles associated [empty]/[notEmpty] operators
        if (key.startsWith("tag_") && !key.includes("[") && !addedTagKeys.has(key) && !processedTagKeys.has(key)) {
            processedTagKeys.add(key);

            const emptyValue = filterString.get(`${key}[empty]`);
            const notEmptyValue = filterString.get(`${key}[notEmpty]`);
            const hasEmptyNull = emptyValue === "true" || notEmptyValue === "true";

            const values = value
                .split(",")
                .map((v) => v.trim())
                .filter((v) => v.length > 0);

            if (hasEmptyNull || values.length > 0) {
                const tagsFilterValue: TagsFilterValue = {};

                if (values.length > 0) {
                    tagsFilterValue.values = values.length === 1 ? values[0] : values;
                }

                if (emptyValue === "true") {
                    tagsFilterValue.emptyNull = "empty";
                } else if (notEmptyValue === "true") {
                    tagsFilterValue.emptyNull = "notEmpty";
                }

                columnFilters.push({ id: key, value: tagsFilterValue });
            }
        }

        // Process standalone tag[empty]/[notEmpty] keys when the base tag key is absent
        // e.g. ?tag_phase-projet[notEmpty]=true (without tag_phase-projet=...)
        const tagEmptyMatch = key.match(/^(tag_.+)\[(empty|notEmpty)\]$/);
        if (tagEmptyMatch) {
            const baseKey = tagEmptyMatch[1];
            const operator = tagEmptyMatch[2] as "empty" | "notEmpty";

            // Only process if the base key wasn't already processed above
            if (!processedTagKeys.has(baseKey) && !addedTagKeys.has(baseKey) && value === "true") {
                processedTagKeys.add(baseKey);

                const tagsFilterValue: TagsFilterValue = { emptyNull: operator };
                columnFilters.push({ id: baseKey, value: tagsFilterValue });
            }
        }
    });

    // Extract jsonAttributes filters from URL parameters
    // Format: jsonAttributes_{id}=value, jsonAttributes_{id}[empty]=true, jsonAttributes_{id}[gte]=10, jsonAttributes_{id}[lte]=20
    const processedJsonAttrKeys = new Set<string>();
    filterString.forEach((value, key) => {
        // Match both plain keys and range operator keys
        const jsonAttrMatch = key.match(/^(jsonAttributes_[^[]+)(?:\[(gte|lte)\])?$/);
        if (jsonAttrMatch) {
            const baseKey = jsonAttrMatch[1];

            if (!processedJsonAttrKeys.has(baseKey)) {
                processedJsonAttrKeys.add(baseKey);

                // Check for empty/notEmpty params
                const emptyValue = filterString.get(`${baseKey}[empty]`);
                const notEmptyValue = filterString.get(`${baseKey}[notEmpty]`);
                const hasEmptyNull = emptyValue === "true" || notEmptyValue === "true";

                // Check for range params (numeric types)
                const minValue = filterString.get(`${baseKey}[gte]`);
                const maxValue = filterString.get(`${baseKey}[lte]`);
                const hasRange = minValue !== null || maxValue !== null;

                // Check for plain text value
                const textValue = filterString.get(baseKey);

                if (hasEmptyNull || hasRange || textValue) {
                    const jsonAttrFilterValue: JsonAttributeFilterValue = {};

                    // Text value (for string types)
                    if (textValue) {
                        jsonAttrFilterValue.value = textValue;
                    }

                    // Range values (for numeric types)
                    if (minValue !== null) {
                        jsonAttrFilterValue.min = Number(minValue);
                    }
                    if (maxValue !== null) {
                        jsonAttrFilterValue.max = Number(maxValue);
                    }

                    // Empty/notEmpty state
                    if (emptyValue === "true") {
                        jsonAttrFilterValue.emptyNull = "empty";
                    } else if (notEmptyValue === "true") {
                        jsonAttrFilterValue.emptyNull = "notEmpty";
                    }

                    columnFilters.push({
                        id: baseKey,
                        value: jsonAttrFilterValue,
                    });
                }
            }
        }
    });

    // Extract range and comparison filters from URL parameters
    // Format: score-5[gte]=10, score-5[lte]=50, score-5[empty]=true
    const processedKeys = new Set<string>();
    filterString.forEach((value, key) => {
        // Check for range operators [gte], [lte] - may combine with [empty]/[notEmpty]
        const rangeMatch = key.match(/^(.+)\[(gte|lte)\]$/);
        if (rangeMatch) {
            const baseKey = rangeMatch[1];
            if (!processedKeys.has(baseKey)) {
                processedKeys.add(baseKey);
                const minValue = filterString.get(`${baseKey}[gte]`);
                const maxValue = filterString.get(`${baseKey}[lte]`);
                const emptyValue = filterString.get(`${baseKey}[empty]`);
                const notEmptyValue = filterString.get(`${baseKey}[notEmpty]`);

                // Check if we have empty/notEmpty - if yes, use ScoreFilterValue
                const hasEmptyNull = emptyValue === "true" || notEmptyValue === "true";

                if (hasEmptyNull) {
                    const scoreFilterValue: ScoreFilterValue = {};
                    if (minValue !== null) scoreFilterValue.min = Number(minValue);
                    if (maxValue !== null) scoreFilterValue.max = Number(maxValue);
                    if (emptyValue === "true") scoreFilterValue.emptyNull = "empty";
                    else if (notEmptyValue === "true") scoreFilterValue.emptyNull = "notEmpty";

                    columnFilters.push({
                        id: baseKey,
                        value: scoreFilterValue,
                    });
                } else if (minValue !== null || maxValue !== null) {
                    // No empty/notEmpty, use RangeValue
                    const rangeValue: RangeValue = {};
                    if (minValue !== null) rangeValue.min = Number(minValue);
                    if (maxValue !== null) rangeValue.max = Number(maxValue);

                    columnFilters.push({
                        id: baseKey,
                        value: rangeValue,
                    });
                }
            }
            return;
        }

        // Check for comparison operators [gt], [lt], [eq]
        const compMatch = key.match(/^(.+)\[(gt|gte|lt|lte|eq)\]$/);
        if (compMatch) {
            const baseKey = compMatch[1];
            const operator = compMatch[2] as ComparisonValue["operator"];

            if (!processedKeys.has(baseKey)) {
                processedKeys.add(baseKey);
                const compValue: ComparisonValue = {
                    operator,
                    value: Number(value),
                };

                columnFilters.push({
                    id: baseKey,
                    value: compValue,
                });
            }
        }

        // Check for standalone empty/notEmpty operators (without range)
        // Skip tag_ keys: they are handled by the tag-specific block above
        const emptyMatch = key.match(/^(.+)\[(empty|notEmpty)\]$/);
        if (emptyMatch && !emptyMatch[1].startsWith("tag_")) {
            const baseKey = emptyMatch[1];
            const filterType = emptyMatch[2];

            if (!processedKeys.has(baseKey) && value === "true") {
                processedKeys.add(baseKey);

                // Check if there's also a range for this key
                const minValue = filterString.get(`${baseKey}[gte]`);
                const maxValue = filterString.get(`${baseKey}[lte]`);

                if (minValue !== null || maxValue !== null) {
                    // Already handled in range section above
                    return;
                }

                // Standalone empty/notEmpty, use ScoreFilterValue
                const scoreFilterValue: ScoreFilterValue = {
                    emptyNull: filterType as EmptyNullValue,
                };

                columnFilters.push({
                    id: baseKey,
                    value: scoreFilterValue,
                });
            }
        }
    });

    // Deduplicate: keep only the last entry per id (last wins, preserving most specific filter)
    const seen = new Set<string>();
    return columnFilters.filter((f) => {
        if (seen.has(f.id)) return false;
        seen.add(f.id);
        return true;
    });
};

export type GridFilters = {
    columnFilters: MRT_ColumnFiltersState;
    setColumnFilters: Dispatch<SetStateAction<MRT_ColumnFiltersState>>;
};

export type GridFiltersUrlSyncOptions = {
    defaultValues: UrlFilterParams;
    isLoading: boolean;
};
export const useGridFiltersUrlSync = (options: GridFiltersUrlSyncOptions): GridFilters => {
    const { defaultValues, isLoading } = options;

    const [searchParams, setSearchParams] = useSearchParams();
    const [columnFilters, _setColumnFilters] = useState<MRT_ColumnFiltersState | null>(null);

    // This as is used because I need the type from REACT Dispatch to use in MRT.
    // But I need a second "internal" state here, and instead of creating 2 states, I'd rather
    // lie here.
    const setColumnFilters = _setColumnFilters as Dispatch<SetStateAction<MRT_ColumnFiltersState>>;

    useEffect(() => {
        // This useEffect is essential since we want to display the Grid with an Empty State;
        // BUT, we absolutely need to instantiate this data AFTER the data is properly fetched;
        if (isLoading === true) return;
        // Hydrate from the URL only while still uninitialised (null). A saved view restored on
        // mount (useViewFilters) applies its filters during the loading phase; without this guard
        // the isLoading->false run re-reads the filter-less URL and clobbers them, leaving the view
        // tab highlighted but its filters dropped until a second click (PPD-1865).
        if (columnFilters !== null) return;

        const initialFilter = urlToMrtFilters(searchParams, defaultValues);
        _setColumnFilters(initialFilter);
    }, [isLoading]);

    useEffect(() => {
        if (isLoading === true) return;
        if (columnFilters === null) return; // means it hasn't been INIT yet.

        // Filter out any filters with undefined/null values before syncing to URL
        // Also filter out tag filters with no meaningful content (empty TagsFilterValue)
        const cleanedFilters = columnFilters.filter((filter) => {
            if (filter.value === undefined || filter.value === null) return false;
            if (filter.id.startsWith("tag_") && typeof filter.value === "object" && !Array.isArray(filter.value)) {
                const tagVal = filter.value as TagsFilterValue;
                const hasValues =
                    tagVal.values !== undefined &&
                    (Array.isArray(tagVal.values) ? tagVal.values.length > 0 : tagVal.values !== "");
                const hasEmptyNull = tagVal.emptyNull !== undefined;
                if (!hasValues && !hasEmptyNull) return false;
            }
            return true;
        });

        // If we removed any filters, update the state to remove them permanently
        if (cleanedFilters.length !== columnFilters.length) {
            _setColumnFilters(cleanedFilters);
            return; // The next effect will handle URL sync
        }

        const newFiltersParams = mrtFiltersToUrl(searchParams, cleanedFilters, defaultValues);
        if (newFiltersParams.toString() === searchParams.toString()) return;

        setSearchParams(newFiltersParams);
    }, [columnFilters, isLoading]);

    return {
        columnFilters: columnFilters ?? [],
        setColumnFilters,
    };
};
